import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadAdminExport, downloadAdminFile } from "@/lib/apiClient";
import { formatIST } from "@/lib/utils";
import { AdminLayout } from "@/components/AdminLayout";
import { DoctorPhoto } from "@/components/DoctorPhoto";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, ChevronLeft, ChevronRight, FileText, ChevronDown, Mail } from "lucide-react";

// Full page for one MR: profile line + every pledge they submitted.
export default function AdminMrDetailPage() {
  const { empCode } = useParams();
  const [mr, setMr] = useState(null);
  const [pledges, setPledges] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // 20 per page: a certificate batch is exactly the page being viewed.
  const limit = 20;

  useEffect(() => {
    api.get("/admin/mrs", { params: { search: empCode, limit: 100 } })
      .then((r) => setMr(r.data.items.find((m) => m.emp_code === empCode) || null))
      .catch(() => {});
  }, [empCode]);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/mrs/${empCode}/pledges`, { params: { page, limit } })
      .then((r) => setPledges(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [empCode, page]);

  const exportMr = () => {
    downloadAdminExport({ mr_emp_code: empCode })
      .then(() => toast.success("Excel exported"))
      .catch(() => toast.error("Export failed"));
  };

  // Certificates come as one PDF per batch of 20. Every batch is offered in
  // the dropdown, so an MR with 47 doctors can be pulled as 1-20, 21-40 and
  // 41-47 without paging the table to reach each one.
  const [certBatch, setCertBatch] = useState(0); // page number being fetched, 0 = idle
  const batches = Array.from({ length: Math.ceil(pledges.total / limit) }, (_, i) => ({
    page: i + 1,
    from: i * limit + 1,
    to: Math.min((i + 1) * limit, pledges.total),
  }));
  const downloadCertificates = (batchPage, label) => {
    setCertBatch(batchPage);
    downloadAdminFile(`/admin/mrs/${empCode}/certificates`, { page: batchPage, limit }, `certificates-${empCode}.pdf`)
      .then(() => toast.success(`Downloaded certificates ${label}`))
      .catch((err) => toast.error(
        // The batch 404s when every pledge in it has been removed.
        err?.status === 404
          ? `No certificates to download in ${label}`
          : "Could not download certificates"
      ))
      .finally(() => setCertBatch(0));
  };

  // Letters are paged exactly like certificates, so letter N of a batch is
  // the doctor on certificate N of the same batch.
  const [letterBatch, setLetterBatch] = useState(0);
  const downloadLetters = (batchPage, label) => {
    setLetterBatch(batchPage);
    downloadAdminFile(`/admin/mrs/${empCode}/letters`, { page: batchPage, limit }, `letters-${empCode}.pdf`)
      .then(() => toast.success(`Downloaded letters ${label}`))
      .catch((err) => toast.error(
        err?.status === 404
          ? `No letters to download in ${label}`
          : "Could not download letters"
      ))
      .finally(() => setLetterBatch(0));
  };

  const totalPages = Math.max(1, Math.ceil(pledges.total / limit));

  return (
    <AdminLayout>
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="mr-detail-page">
        <Link to="/admin/mrs" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2]" data-testid="mr-detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to MR Team
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display font-extrabold text-2xl text-slate-900">
              {mr ? mr.name : `MR ${empCode}`}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Emp Code: <span className="font-semibold text-slate-700">{empCode}</span>
              {mr?.division && <> · <Badge variant="outline" className="border-blue-200 bg-blue-50 text-[#0056D2]">{mr.division}</Badge></>}
              {mr?.designation && <> · {mr.designation}</>}
              {mr?.hq && <> · HQ: {mr.hq}</>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="mr-certs-btn"
                  disabled={certBatch > 0 || loading || pledges.total === 0}
                  variant="outline"
                  className="rounded-full border-slate-300"
                  title="Download certificates as PDFs of 20 doctors each"
                >
                  {certBatch > 0
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <FileText className="w-4 h-4 mr-2" />}
                  Certificates
                  <ChevronDown className="w-4 h-4 ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="text-xs font-normal text-slate-500">
                  One PDF per 20 doctors
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {batches.map((b) => {
                  const label = `${b.from}-${b.to}`;
                  return (
                    <DropdownMenuItem
                      key={b.page}
                      data-testid={`mr-certs-batch-${b.page}`}
                      onSelect={() => downloadCertificates(b.page, label)}
                      className="cursor-pointer"
                    >
                      <FileText className="w-3.5 h-3.5 mr-2 text-slate-400" />
                      Doctors {label}
                      <span className="ml-auto pl-4 text-xs text-slate-400">
                        {b.to - b.from + 1}
                      </span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="mr-letters-btn"
                  disabled={letterBatch > 0 || loading || pledges.total === 0}
                  variant="outline"
                  className="rounded-full border-slate-300"
                  title="Download thank-you letters as PDFs of 20 doctors each"
                >
                  {letterBatch > 0
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Mail className="w-4 h-4 mr-2" />}
                  Letters
                  <ChevronDown className="w-4 h-4 ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="text-xs font-normal text-slate-500">
                  One PDF per 20 doctors
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {batches.map((b) => {
                  const label = `${b.from}-${b.to}`;
                  return (
                    <DropdownMenuItem
                      key={b.page}
                      data-testid={`mr-letters-batch-${b.page}`}
                      onSelect={() => downloadLetters(b.page, label)}
                      className="cursor-pointer"
                    >
                      <Mail className="w-3.5 h-3.5 mr-2 text-slate-400" />
                      Doctors {label}
                      <span className="ml-auto pl-4 text-xs text-slate-400">
                        {b.to - b.from + 1}
                      </span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button data-testid="mr-export-btn" onClick={exportMr} variant="outline" className="rounded-full border-slate-300">
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
          </div>
        </div>

        <h3 className="mt-6 font-display font-bold text-slate-900">Pledges submitted ({pledges.total})</h3>
        <div className="mt-3 overflow-x-auto">
          <Table data-testid="mr-detail-pledges">
            <TableHeader>
              <TableRow>
                <TableHead>Photo</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Reg No</TableHead>
                <TableHead>Specialty</TableHead>
                <TableHead>City / State</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pledged At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></TableCell></TableRow>
              ) : pledges.items.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">No pledges from this MR yet</TableCell></TableRow>
              ) : (
                pledges.items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell><DoctorPhoto pledge={p} /></TableCell>
                    <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
                    <TableCell className="text-slate-600">{p.reg_no}</TableCell>
                    <TableCell className="text-slate-600">{p.specialty}</TableCell>
                    <TableCell className="text-slate-600">{p.city}, {p.state}</TableCell>
                    <TableCell className="text-slate-600">{p.email}</TableCell>
                    <TableCell className="text-slate-600">{p.mobile}</TableCell>
                    <TableCell className="text-slate-600 capitalize">{p.status}</TableCell>
                    <TableCell className="text-slate-600 text-xs whitespace-nowrap">{formatIST(p.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} size="sm" variant="outline" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
            <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} size="sm" variant="outline" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
